import tenantConfig from './index'
import type { TenantConfig } from './types'

/**
 * Runtime SEO for the active tenant.
 *
 * This is a client-rendered SPA, so the static tags in public/index.html are
 * the crawler-facing baseline (filled at build time from REACT_APP_* env vars).
 * This module enhances/overrides them once the tenant is resolved and injects
 * JSON-LD structured data (Organization, WebSite + SearchAction, FAQPage) that
 * search engines and AI answer engines use to understand the site.
 */

const STRUCTURED_DATA_ID = 'tenant-structured-data'

/** Resolve a possibly-relative asset/path to an absolute URL. */
function toAbsoluteUrl(path: string, base: string): string {
  try {
    return new URL(path, base).href
  } catch {
    return path
  }
}

/** Effective SEO values, deriving sensible defaults when a tenant omits them. */
interface ResolvedSeo {
  title: string
  description: string
  keywords: string[]
  ogImage: string
  locale: string
  areaServed: string[]
  searchPathTemplate?: string
}

function resolveSeo(config: TenantConfig): ResolvedSeo {
  const { name, tagline, content, seo } = config
  return {
    title: seo?.title ?? `${name} — ${tagline}`,
    description:
      seo?.description ?? content.heroDescription ?? `${name}. ${tagline}.`,
    keywords: seo?.keywords ?? [name, tagline, 'insurance', 'Kenya'],
    ogImage: seo?.ogImage ?? config.logo,
    locale: seo?.locale ?? 'en',
    areaServed: seo?.areaServed ?? [],
    searchPathTemplate: seo?.searchPathTemplate
  }
}

/** Create-or-update a <meta> tag keyed by name or property. */
function upsertMeta(
  key: 'name' | 'property',
  value: string,
  content: string
): void {
  if (!content) return
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${key}="${value}"]`
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(key, value)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Create-or-update a <link> tag keyed by its rel. */
function upsertLink(rel: string, href: string): void {
  if (!href) return
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** Build the JSON-LD graph: Organization, WebSite (+search) and FAQ. */
function buildStructuredData(
  config: TenantConfig,
  seo: ReturnType<typeof resolveSeo>,
  origin: string
): Record<string, unknown> {
  const {
    name,
    websiteUrl,
    supportEmail,
    supportPhone,
    twitterHandle,
    content
  } = config

  const siteUrl = websiteUrl || origin
  const logoUrl = toAbsoluteUrl(config.logo, origin)
  const sameAs = twitterHandle
    ? [`https://twitter.com/${twitterHandle.replace(/^@/, '')}`]
    : []

  const organization = {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name,
    url: siteUrl,
    logo: logoUrl,
    image: toAbsoluteUrl(seo.ogImage ?? config.logo, origin),
    description: seo.description,
    email: supportEmail,
    telephone: supportPhone,
    sameAs,
    areaServed: seo.areaServed,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: supportEmail,
      telephone: supportPhone,
      areaServed: seo.areaServed
    }
  }

  const website: Record<string, unknown> = {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name,
    description: seo.description,
    publisher: { '@id': `${siteUrl}/#organization` },
    inLanguage: seo.locale.replace('_', '-')
  }

  // Add a SearchAction (Google sitelinks search box) only when the tenant
  // declares a real search endpoint — pointing at a 404 is invalid markup.
  if (seo.searchPathTemplate) {
    const urlTemplate = toAbsoluteUrl(
      seo.searchPathTemplate.replace('{q}', '{search_term_string}'),
      siteUrl
    )
    website.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate
      },
      'query-input': 'required name=search_term_string'
    }
  }

  // Main navigation — helps search engines surface sitelinks under the result.
  const nav: { name: string; path: string }[] = [
    { name: 'Home', path: '/' },
    { name: 'Insurance Products', path: '/insurance-products' },
    { name: 'Support', path: '/support' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms-conditions' }
  ]
  const siteNavigation = {
    '@type': 'SiteNavigationElement',
    '@id': `${siteUrl}/#navigation`,
    name: nav.map((n) => n.name),
    url: nav.map((n) => `${siteUrl}${n.path}`)
  }

  const graph: Record<string, unknown>[] = [
    organization,
    website,
    siteNavigation
  ]

  // FAQ structured data — drives rich results / AI answer engines.
  const faqItems = content.faqItems ?? []
  if (faqItems.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${siteUrl}/#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}

/** Inject (or replace) the tenant JSON-LD <script> in <head>. */
function applyStructuredData(data: Record<string, unknown>): void {
  let script = document.getElementById(
    STRUCTURED_DATA_ID
  ) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.id = STRUCTURED_DATA_ID
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data)
}

/**
 * Apply all SEO metadata for the active tenant: title, description, keywords,
 * canonical, Open Graph, Twitter cards and JSON-LD structured data.
 */
function applySeo(): void {
  if (typeof document === 'undefined') return

  const config = tenantConfig
  const seo = resolveSeo(config)
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : config.websiteUrl
  const canonical = config.websiteUrl
    ? toAbsoluteUrl(window.location.pathname, config.websiteUrl)
    : window.location.href
  const ogImageUrl = toAbsoluteUrl(seo.ogImage ?? config.logo, origin)

  // Title + primary meta
  document.title = seo.title
  upsertMeta('name', 'description', seo.description)
  upsertMeta('name', 'keywords', seo.keywords.join(', '))
  upsertMeta('name', 'author', config.name)
  upsertMeta('name', 'robots', 'index, follow')
  upsertMeta('name', 'theme-color', config.theme.emailPrimaryHex)

  upsertLink('canonical', canonical)

  // Open Graph
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', config.name)
  upsertMeta('property', 'og:title', seo.title)
  upsertMeta('property', 'og:description', seo.description)
  upsertMeta('property', 'og:url', canonical)
  upsertMeta('property', 'og:image', ogImageUrl)
  upsertMeta('property', 'og:locale', seo.locale)

  // Twitter cards
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', seo.title)
  upsertMeta('name', 'twitter:description', seo.description)
  upsertMeta('name', 'twitter:image', ogImageUrl)
  if (config.twitterHandle) {
    upsertMeta('name', 'twitter:site', config.twitterHandle)
    upsertMeta('name', 'twitter:creator', config.twitterHandle)
  }

  // Structured data (JSON-LD)
  applyStructuredData(buildStructuredData(config, seo, origin))
}

export default applySeo
